export type NavLocation = { pathname: string; search: string };

type Target = { path: string; pathname: string; query: [string, string][] };

const withoutTrailingSlash = (pathname: string) =>
  pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;

function targetOf(path: string): Target {
  const [pathname = '/', search = ''] = path.split('?');
  return {
    path,
    pathname: withoutTrailingSlash(pathname),
    query: [...new URLSearchParams(search).entries()],
  };
}

/**
 * How closely a link names the address, as `[path segments, query parameters]`,
 * or `null` when it does not name it. A plain link names its own path and every
 * path under it, whatever the query — the dashboard `/` only itself. A link
 * carrying a query is a saved view of one list, so it names only that exact path
 * with every parameter it carries, and keeps naming it while the operator adds
 * filters of their own.
 */
function closeness(target: Target, pathname: string, params: URLSearchParams): number[] | null {
  if (target.query.length > 0) {
    if (pathname !== target.pathname) return null;
    if (!target.query.every(([key, value]) => params.getAll(key).includes(value))) return null;
  } else if (target.pathname === '/') {
    if (pathname !== '/') return null;
  } else if (pathname !== target.pathname && !pathname.startsWith(`${target.pathname}/`)) {
    return null;
  }
  const segments = target.pathname.split('/').filter(Boolean).length;
  return [segments, target.query.length];
}

const compare = (left: number[], right: number[]) =>
  (left[0] ?? 0) - (right[0] ?? 0) || (left[1] ?? 0) - (right[1] ?? 0);

/**
 * The links to highlight for an address: of those that name it, only the
 * closest. `NavLink` compares paths alone, so every `/donations?…` saved view
 * lit up with the list, and `/reconciliation` stayed lit on its payouts screen.
 */
export function activeNavPaths(paths: readonly string[], location: NavLocation): Set<string> {
  const pathname = withoutTrailingSlash(location.pathname);
  const params = new URLSearchParams(location.search);
  const named = paths.flatMap((path) => {
    const score = closeness(targetOf(path), pathname, params);
    return score ? [{ path, score }] : [];
  });
  const best = named.reduce<number[] | null>(
    (closest, { score }) => (closest === null || compare(score, closest) > 0 ? score : closest),
    null,
  );
  return new Set(
    named
      .filter(({ score }) => best !== null && compare(score, best) === 0)
      .map(({ path }) => path),
  );
}
